import { useEffect, useRef, useState, useCallback } from 'react'
import AnimatedLetters from '../AnimatedLetters'
import './index.scss'
import Loader from 'react-loaders'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'



// ─── Images ───────────────────────────────────────────────────────────────────
import EndCreditsPreview from '../../assets/images/project-preview.png'
import EndCreditsGif     from '../../assets/images/project-preview.gif'
import ProjectTwoPreview from '../../assets/images/SJSU.png'
import AA1  from '../../assets/images/AA1LV.png'
import AA2  from '../../assets/images/AA2LVSize.png'
import AA3  from '../../assets/images/AA3MCU_Whole.png'
import AA4  from '../../assets/images/AA4MCUNet.png'
import AA5  from '../../assets/images/AA5MCUBet.png'
import AA6  from '../../assets/images/AA6MCUClo.png'
import AA7  from '../../assets/images/AA7MCUEigenVec.png'
import AA8  from '../../assets/images/AA8MCULink.png'
import AA9  from '../../assets/images/AA9arc1.png'
import AA10 from '../../assets/images/AA10arc2.png'
import AA11 from '../../assets/images/AA11ClusterVisual.png'
import AA12 from '../../assets/images/AA12ConfData.png'
gsap.registerPlugin(ScrollTrigger)
gsap.config({ force3D: true })

// ─── Project Data ─────────────────────────────────────────────────────────────
const projectsData = [
    {
        id: 1,
        index: '01',
        title: "Master's Thesis",
        subtitle: 'SJSU MS Computer Science',
        url: 'https://scholarworks.sjsu.edu/etd_projects/1547/',
        images: [ProjectTwoPreview],
        gif: null,
        acts: [
            // ── Act 1: Hero ──────────────────────────────────────────────────
            {
                type: 'hero',
                description: `A cross-platform, cross-architecture performance analysis of machine learning across Apple Silicon M3 Pro (MPS), NVIDIA RTX 3070 (CUDA), and software-simulated neuromorphic computing. ResNet-18 and a Convolutional Spiking Neural Network were benchmarked on frame-based (CIFAR-10) and event-based (DVSGesture) datasets — measuring accuracy, training time, memory, and energy consumption.`,
                tech: ['PyTorch', 'snnTorch', 'Metal Performance Shaders', 'CUDA', 'ResNet-18', 'CNNs', 'CSNNs', 'CIFAR-10', 'DVSGesture', 'Neuromorphic Computing', 'Benchmarking'],
            },

            // ── Act 2: Experiment 1 — CIFAR-10 full story ───────────────────
            {
                type: 'experiment',
                experimentTag: 'Experiment 1',
                label: 'CIFAR-10 · Frame-Based Data',
                summary: 'On frame-based data, ResNet-18 achieved near-identical accuracy on both platforms — but Apple consumed up to 8× less power. The extended time-step analysis revealed a logarithmic accuracy curve with near-linear energy growth.',
                comparison: {
                    columns: ['Apple Silicon · MPS', 'NVIDIA RTX 3070 · CUDA'],
                    rows: [
                        { metric: 'ResNet Accuracy',   values: ['83.84%',   '84.09%'],   winner: 1 },
                        { metric: 'ResNet Avg Power',  values: ['10.9 W',   '60.5 W'],   winner: 0 },
                        { metric: 'ResNet Energy',     values: ['1.57 Wh',  '6.06 Wh'],  winner: 0 },
                        { metric: 'ResNet Train Time', values: ['541 s',    '376 s'],     winner: 1 },
                        { metric: 'CSNN Accuracy',     values: ['72.5%',    '71.45%'],    winner: 0 },
                        { metric: 'CSNN Avg Power',    values: ['16.5 W',   '140.8 W'],   winner: 0 },
                        { metric: 'CSNN Energy',       values: ['8.90 Wh',  '27.82 Wh'],  winner: 0 },
                        { metric: 'CSNN Train Time',   values: ['1,968 s',  '726 s'],     winner: 1 },
                    ],
                },
                timesteps: {
                    intro: 'T=1→12 on Apple (40 epochs) — accuracy follows a logarithmic curve while energy grows nearly linearly.',
                    columns: ['T', 'Accuracy', 'Train Time', 'Avg Power', 'Total Energy'],
                    rows: [
                        { label: '1',  values: ['59.03%', '1,043 s', '10.87 W', '3.04 Wh'],   best: false },
                        { label: '3',  values: ['73.58%', '2,603 s', '16.23 W', '11.62 Wh'],  best: false  },
                        { label: '6',  values: ['78.75%', '4,969 s', '17.28 W', '23.72 Wh'],  best: false },
                        { label: '9',  values: ['81.13%', '7,358 s', '17.68 W', '35.98 Wh'],  best: false },
                        { label: '12', values: ['81.82%', '9,759 s', '17.87 W', '48.27 Wh'],  best: true },
                    ],
                    callouts: [
                        { value: '+22.79 %', label: 'Accuracy gain T=1 \u2192 T=12' },
                        { value: '15.9×',   label: 'Energy increase' },
                        { value: 'T=3',     label: 'Best efficiency (with reasonable acc.)' },
                    ],
                },
            },

            // ── Act 3: Experiment 2 — DVSGesture full story ─────────────────
            {
                type: 'experiment',
                experimentTag: 'Experiment 2',
                label: 'DVSGesture · Event-Based Data',
                summary: 'Event-based data flipped the script: Apple ran the CSNN 2× faster than NVIDIA while using 16× less energy. The non-monotonic time-step accuracy pattern — peaking at T=4 — highlights a fundamental difference in how CSNNs handle event-based data.',
                comparison: {
                    columns: ['Apple Silicon · MPS', 'NVIDIA RTX 3070 · CUDA'],
                    rows: [
                        { metric: 'CNN Accuracy',      values: ['86.36%',   '89.02%'],    winner: 1 },
                        { metric: 'CNN Avg Power',     values: ['10.8 W',   '139.7 W'],   winner: 0 },
                        { metric: 'CNN Energy',        values: ['8.21 Wh',  '42.27 Wh'],  winner: 0 },
                        { metric: 'CNN Train Time',    values: ['2,519 s',  '948 s'],      winner: 1 },
                        { metric: 'CSNN Accuracy',     values: ['80.30%',   '77.27%'],    winner: 0 },
                        { metric: 'CSNN Avg Power',    values: ['12.5 W',   '95.5 W'],    winner: 0 },
                        { metric: 'CSNN Energy',       values: ['15.70 Wh', '229.82 Wh'], winner: 0 },
                        { metric: 'CSNN Train Time',   values: ['4,102 s',  '8,197 s'],   winner: 0 },
                    ],
                },
                timesteps: {
                    intro: 'T=4→20 on Apple (40 epochs) — accuracy peaks at T=4 then dips. T=8 and T=12 underperform, suggesting temporal aliasing at intermediate configurations.',
                    columns: ['T', 'Accuracy', 'Train Time', 'Avg Power', 'Total Energy'],
                    rows: [
                        { label: '4',  values: ['81.06%', '1,336 s',  '12.80 W', '5.03 Wh'],  best: true  },
                        { label: '8',  values: ['73.86%', '2,610 s',  '12.34 W', '9.61 Wh'],  best: false },
                        { label: '12', values: ['74.62%', '3,939 s',  '12.41 W', '14.60 Wh'], best: false },
                        { label: '16', values: ['79.17%', '5,214 s',  '12.39 W', '19.34 Wh'], best: false },
                        { label: '20', values: ['80.30%', '11,969 s', '7.35 W',  '25.13 Wh'], best: false },
                    ],
                    callouts: [
                        { value: 'T=4',   label: 'Highest accuracy & lowest cost' },
                        { value: '4.99×', label: 'Energy increase T=4→20' },
                        { value: '2.07×', label: 'Apple speedup on CSNN' },
                    ],
                },
            },

            // ── Act 4: Key Findings ──────────────────────────────────────────
            {
                type: 'stats',
                label: 'Key Findings',
                stats: [
                    { value: '8×',    label: 'Apple used 8× less power on CSNN CIFAR-10' },
                    { value: '16×',   label: 'Apple used 16× less energy on CSNN DVSGesture' },
                    { value: '2.07×', label: 'Apple speedup over NVIDIA on event-based CSNN' },
                    { value: '15.9×', label: 'Energy cost of pushing T=1 to T=12 for +22.79% accuracy' },
                ],
            },

            // ── Act 5: Conclusion Quote ──────────────────────────────────────
            {
                type: 'quote',
                text: 'Optimal platform-architecture combinations depend on specific application requirements. CSNNs are fundamentally better aligned with event-based data — and careful temporal configuration is critical for practical deployment.',
                attribution: 'Saini & Andreopoulos · Inventions 2025',
            },
        ],
    },

    {
        id: 2,
        index: '02',
        title: 'End-Credits',
        subtitle: 'Full-Stack Web Application',
        url: 'https://end-credits.com',
        images: [EndCreditsPreview],
        gif: null,
        acts: [
            {
                type: 'hero',
                description: `End-Credits helps movie fans discover whether films include post-credit scenes — so you never miss a hidden extra, a tease, or the scene that changes everything. Built with React and Flask, it integrates the TMDb API, Google Custom Search, web scraping, and the Groq LLM for AI-generated summaries cross-referenced against Wikipedia.`,
                tech: ['React', 'Flask', 'Python', 'TMDb API', 'LLMs (Groq)', 'Web Scraping', 'Google Custom Search', 'Wikipedia Validation', 'Render', 'Tailwind CSS'],
            },
            {
                type: 'showcase',
                image: EndCreditsGif,
                label: 'Live Demo',
            },
            {
                type: 'details',
                leftLabel: 'How It Works',
                leftText: `A user searches for any film. The app queries TMDb for metadata, then fires Google Custom Search to find relevant articles across the web. BeautifulSoup scrapes those pages and the Groq LLM distills the findings into a clean summary. A final Wikipedia cross-reference validates the result before it's returned to the front-end — all in a single request cycle.`,
                rightLabel: 'Technologies',
            },
            {
                type: 'quote',
                text: 'So you never miss a hidden extra, a post-credit tease, or the scene that changes everything.',
                attribution: 'end-credits.com',
            },
        ],
    },

    {
        id: 3,
        index: '03',
        title: 'Actor Analysis',
        subtitle: 'Graph Theory & Network Science',
        url: null,
        images: [AA1, AA2, AA3, AA4, AA5, AA6, AA7, AA8],
        gif: null,
        acts: [
            {
                type: 'hero',
                description: `Models the film industry as a large-scale collaboration graph using TMDb data from 2005–2024. A weighted co-starring network of 5,694 actors across 2,815 films was constructed, capturing 26,543 relationships. Centrality analysis identified structural bridge actors, while Louvain community detection found 145 distinct communities.`,
                tech: ['Python', 'NetworkX', 'Graph Theory', 'Louvain Community Detection', 'Centrality Metrics', 'Link Prediction', 'Adamic–Adar', 'Jaccard', 'TMDb API', 'Gephi'],
            },
            {
                type: 'details',
                leftLabel: 'Methodology',
                leftText: `Data was collected programmatically via the TMDb API and filtered to remove low-collaboration entries. A weighted graph was built where edge weight reflects shared films. Centrality metrics (degree, betweenness, eigenvector) identified structurally significant actors. Louvain community detection was applied for clustering, and link prediction evaluated using Jaccard, Adamic–Adar, and Preferential Attachment coefficients. A focused MCU subnetwork amplified influence patterns.`,
                rightLabel: 'Technologies',
            },
            {
                type: 'gallery',
                label: 'Network Structure',
                images: [AA2, AA3, AA4],
            },
            {
                type: 'fullpage',
                label: 'MCU Betweenness Centrality',
                images: [AA5],
            },
            {
                type: 'gallery',
                label: 'Centrality & Prediction',
                images: [AA6, AA7, AA8],
            },
            {
                type: 'stats',
                label: 'By the Numbers',
                stats: [
                    { value: '5,694', label: 'Actors Mapped' },
                    { value: '2,815', label: 'Films Analyzed' },
                    { value: '145',   label: 'Communities Detected' },
                    { value: '86%',   label: 'Link Prediction Accuracy' },
                ],
            },
        ],
    },

    {
        id: 4,
        index: '04',
        title: 'Distributed Data Mining',
        subtitle: 'Scalable ML Workflows',
        url: null,
        images: [AA9, AA11],
        gif: null,
        acts: [
            {
                type: 'hero',
                description: `Distributed data mining and ML workflows spanning Hadoop, Apache Mahout, and Apache Spark. Built MapReduce jobs for frequent itemset mining using pairs and stripes design patterns. Mahout handled K-Means clustering on transaction and crime datasets. Spark-based ML pipelines containerized with Docker trained classification models on the UCI Adult dataset.`,
                tech: ['Python', 'Hadoop', 'HDFS', 'MapReduce', 'Pairs & Stripes', 'Apache Mahout', 'K-Means Clustering', 'Apache Spark', 'PySpark MLlib', 'Docker', 'UCI Adult Dataset'],
            },
            {
                type: 'details',
                leftLabel: 'Architecture',
                leftText: `Three distinct frameworks handled different layers. Hadoop MapReduce aggregated retail transaction data for frequent itemset mining using both pairs and stripes patterns — directly demonstrating memory-computation tradeoffs. Mahout extended this with scalable K-Means clustering across multiple datasets. The final Spark layer ran inside Docker with a complete PySpark pipeline: feature encoding, vector assembly, and distributed model training and evaluation on the UCI Adult dataset.`,
                rightLabel: 'Technologies',
            },
            {
                type: 'fullpage',
                label: 'Cluster Visualization',
                images: [AA11],
            },
            {
                type: 'stats',
                label: 'By the Numbers',
                stats: [
                    { value: '3',         label: 'Distributed Frameworks' },
                    { value: '2',         label: 'MapReduce Design Patterns' },
                    { value: 'Docker',    label: 'Deployment Target' },
                    { value: 'UCI Adult', label: 'Classification Benchmark' },
                ],
            },
        ],
    },

    {
        id: 5,
        index: '05',
        title: 'Conference Data Pipeline',
        subtitle: 'Distributed ETL System',
        url: null,
        images: [AA10, AA12],
        gif: null,
        acts: [
            {
                type: 'hero',
                description: `End-to-end distributed pipeline to collect, clean, and analyze large-scale academic conference data. A custom Python web crawler extracted structured data from 300+ conference pages. OpenRefine normalized and deduplicated the raw dataset from 949 down to 770 records. Multiple Hadoop MapReduce jobs then performed distributed aggregation and trend analysis.`,
                tech: ['Python', 'requests', 'BeautifulSoup', 'OpenRefine', 'Hadoop', 'HDFS', 'MapReduce', 'Custom Mappers & Reducers', 'ETL Pipeline Design', 'Data Visualization'],
            },
            {
                type: 'details',
                leftLabel: 'Pipeline Stages',
                leftText: `Stage 1 — Crawl: A Python crawler navigated 300+ conference pages, extracting structured fields (name, category, location, year) using BeautifulSoup. Stage 2 — Clean: OpenRefine deduplicated and normalized the raw data, reducing 949 entries to 770 reliable records. Stage 3 — Analyze: Custom Python mapper/reducer scripts deployed on HDFS computed top hosting cities, category-to-city distributions, and city-year frequency trends across the full dataset.`,
                rightLabel: 'Technologies',
            },
            {
                type: 'fullpage',
                label: 'Conference Analysis',
                images: [AA12],
            },
            {
                type: 'stats',
                label: 'By the Numbers',
                stats: [
                    { value: '300+', label: 'Conference Pages Crawled' },
                    { value: '949',  label: 'Raw Records Collected' },
                    { value: '770',  label: 'Clean Records After Refine' },
                    { value: '4',    label: 'MapReduce Jobs Deployed' },
                ],
            },
        ],
    },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getScroller = () => document.querySelector('.page')
const ACT_BG_CYCLE = ['bg-deep', 'bg-panel', 'bg-alt', 'bg-deep', 'bg-panel']

const usePinnedAct = (ref, scroller, pinDistance, onTl, onScrollTl) => {
    useEffect(() => {
        const el  = ref.current
        const scr = scroller || getScroller()
        if (!el || !scr) return
        const ctx = gsap.context(() => {
            // Layer 1: entrance animation — plays/reverses on scroll in/out
            const tl = gsap.timeline({ paused: true })
            onTl(tl, el)

            ScrollTrigger.create({
                trigger: el, scroller: scr,
                start: 'top 80%',
                end: 'bottom 20%',
                onEnter: () => tl.restart(),
                onEnterBack: () => tl.restart(),
                onLeave: () => tl.progress(1),
                onLeaveBack: () => tl.reverse(),
            })

            // Layer 2: scroll-linked motion — scrubbed, no pin
            if (onScrollTl) {
                const stl = gsap.timeline({
                    scrollTrigger: {
                        trigger: el, scroller: scr,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 0.6,
                    },
                })
                onScrollTl(stl, el)
            }
        }, el)
        return () => ctx.revert()
    }, [ref, scroller, pinDistance, onTl, onScrollTl])
}

// ─── Card Deck ────────────────────────────────────────────────────────────────
const CardDeck = ({ images }) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [isAnimating, setIsAnimating] = useState(false)
    const cardRefs = useRef([])
    const total    = images.length

    useEffect(() => {
        cardRefs.current.forEach((card, i) => {
            if (!card) return
            gsap.set(card, { x:i*14, y:i*-7, rotate:i*3.5, zIndex:Math.min(total,5)-i, opacity:i<5?1:0 })
        })
    }, [total])

    const advance = useCallback(() => {
        if (isAnimating) return
        setIsAnimating(true)
        const cards = cardRefs.current
        const top   = cards[0]
        const tl = gsap.timeline({
            onComplete: () => { setActiveIndex(p => (p+1)%total); setIsAnimating(false) }
        })
        tl.to(top, { x:200, y:-90, rotate:28, opacity:0, duration:0.55, ease:'power2.in' })
        cards.slice(1).forEach((card, i) => {
            if (!card) return
            tl.to(card, { x:i*14, y:i*-7, rotate:i*3.5, zIndex:Math.min(total,5)-i, duration:0.5, ease:'power2.out' }, '<0.07')
        })
        tl.set(top, { x:(total-1)*14, y:(total-1)*-7, rotate:(total-1)*3.5, opacity:0, zIndex:0 })
        tl.to(top, { opacity:total<=5?1:0, duration:0.3 })
        tl.call(() => { const f = cardRefs.current.shift(); cardRefs.current.push(f) })
    }, [isAnimating, total])

    return (
        <div className="card-deck-wrapper" onClick={advance}>
            <div className="card-deck">
                {images.map((img, i) => {
                    const vi = (activeIndex+i)%total
                    return (
                        <div key={i} ref={el => cardRefs.current[i]=el} className="deck-card">
                            <img src={images[vi]} alt={`View ${vi+1}`} draggable={false}/>
                        </div>
                    )
                })}
            </div>
            <div className="deck-meta">
                <span className="deck-counter">
                    <span className="deck-current">{String(activeIndex+1).padStart(2,'0')}</span>
                    <span className="deck-sep"> — </span>
                    <span className="deck-total">{String(total).padStart(2,'0')}</span>
                </span>
                <span className="deck-hint">{isAnimating?'·':'CLICK TO ADVANCE'}</span>
            </div>
            <div className="deck-dots">
                {images.map((_,i) => <span key={i} className={`deck-dot ${i===activeIndex?'active':''}`}/>)}
            </div>
        </div>
    )
}

// ─── Act: Hero ────────────────────────────────────────────────────────────────
const ActHero = ({ project, act, scroller, bgClass }) => {
    const ref = useRef(null)
    const lightboxRef = useRef(null)
    const [expandedSrc, setExpandedSrc] = useState(null)

    const openLightbox = useCallback((src) => setExpandedSrc(src), [])
    const closeLightbox = useCallback(() => {
        const lb = lightboxRef.current
        if (!lb) { setExpandedSrc(null); return }
        gsap.to(lb.querySelector('.lb-image'), {
            scale: 0.85, opacity: 0, filter: 'blur(10px)',
            duration: 0.45, ease: 'power2.in',
        })
        gsap.to(lb.querySelector('.lb-backdrop'), {
            opacity: 0, duration: 0.45, ease: 'power2.in',
            onComplete: () => {
                setExpandedSrc(null)
                ScrollTrigger.refresh()
            },
        })
    }, [])

    useEffect(() => {
        if (!expandedSrc || !lightboxRef.current) return
        const lb = lightboxRef.current
        gsap.set(lb.querySelector('.lb-image'), { scale: 0.6, opacity: 0, filter: 'blur(14px)' })
        gsap.set(lb.querySelector('.lb-backdrop'), { opacity: 0 })
        gsap.to(lb.querySelector('.lb-backdrop'), { opacity: 1, duration: 0.5, ease: 'power2.out' })
        gsap.to(lb.querySelector('.lb-image'), {
            scale: 1, opacity: 1, filter: 'blur(0px)',
            duration: 0.7, ease: 'power3.out', delay: 0.07,
        })
    }, [expandedSrc])

    useEffect(() => {
        if (!expandedSrc) return
        const onKey = (e) => { if (e.key === 'Escape') closeLightbox() }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [expandedSrc, closeLightbox])

    useEffect(() => {
        if (!expandedSrc) return
        const scr = scroller || getScroller()
        if (!scr) return
        let listening = false
        const onScroll = () => { if (listening) closeLightbox() }
        const timer = setTimeout(() => {
            listening = true
            scr.addEventListener('scroll', onScroll, { once: true })
        }, 400)
        return () => { clearTimeout(timer); scr.removeEventListener('scroll', onScroll) }
    }, [expandedSrc, scroller, closeLightbox])

    usePinnedAct(ref, scroller, 680, (tl, el) => {
        tl.fromTo(el.querySelector('.act-index'),    {opacity:0,y:30},  {opacity:1,y:0,duration:0.7,ease:'power2.out'},0)
        tl.fromTo(el.querySelector('.act-subtitle'), {opacity:0,x:-24}, {opacity:1,x:0,duration:0.7,ease:'power2.out'},0.15)
        tl.fromTo(el.querySelectorAll('.hero-title-word'), {y:'110%',opacity:0}, {y:'0%',opacity:1,stagger:0.12,duration:1.0,ease:'power3.out'},0.3)
        tl.fromTo(el.querySelector('.act-visual'),   {scale:1.08,opacity:0}, {scale:1,opacity:1,duration:1.1,ease:'power2.out'},0.2)
        tl.fromTo(el.querySelector('.hero-rule'),    {scaleX:0}, {scaleX:1,duration:0.7,ease:'power2.out'},0.7)
        tl.fromTo(el.querySelectorAll('.reveal-item'), {opacity:0,y:24}, {opacity:1,y:0,stagger:0.12,duration:0.85,ease:'power2.out'},0.85)
    }, (stl, el) => {
        const text   = el.querySelector('.act-hero-text')
        const visual = el.querySelector('.act-visual')
        if (text)   stl.fromTo(text,   { y: 40 },  { y: -40, ease: 'none' }, 0)
        if (visual) stl.fromTo(visual, { y: -30 }, { y: 30,  ease: 'none' }, 0)
    })

    const hasGalleryActs = project.acts.some(a => a.type === 'gallery' || a.type === 'fullpage')
    const hasMultiple = !hasGalleryActs && project.images && project.images.length > 1
    const heroSrc     = project.images[0]

    return (
        <div ref={ref} className={`act act-hero ${bgClass}`}>
            <span className="panel-number">{project.index}</span>
            <div className="act-hero-inner">
                <div className="act-hero-text">
                    <span className="act-index">{project.index}</span>
                        <p className="act-subtitle">{project.subtitle}</p>
                        <h2 className="act-hero-title">
                            {project.title.split(' ').map((word,i) => (
                                <span className="hero-title-word-wrap" key={i}><span className="hero-title-word">{word}</span></span>
                            ))}
                        </h2>
                        <div className="hero-rule"/>
                        <p className="reveal-item act-hero-desc">{act.description}</p>
                        <div className="reveal-item tech-stack">
                            {act.tech.map((t,i) => <span key={i} className="tech-tag">{t}</span>)}
                        </div>
                        {project.url && <a className="reveal-item flat-button" href={project.url} target="_blank" rel="noopener noreferrer">VIEW LIVE</a>}
                    </div>

                    <div className="act-visual">
                        {hasMultiple ? (
                            <CardDeck images={project.images}/>
                        ) : (
                            <div className="image-frame">
                                <img src={heroSrc} alt={project.title} className="panel-image"
                                     onClick={project.url?()=>window.open(project.url,'_blank'):()=>openLightbox(heroSrc)}
                                     style={{cursor:project.url?'pointer':'zoom-in'}}/>
                                <div className="corner tl"/><div className="corner tr"/>
                                <div className="corner bl"/><div className="corner br"/>
                            </div>
                        )}
                    </div>
                </div>
            {expandedSrc && (
                <div ref={lightboxRef} className="gallery-lightbox" onClick={closeLightbox}>
                    <div className="lb-backdrop" />
                    <img src={expandedSrc} className="lb-image" alt="Expanded view" />
                </div>
            )}
        </div>
    )
}

// ─── Act: Showcase (dedicated GIF / image section with scroll physics) ───────
const ActShowcase = ({ project, act, scroller, bgClass }) => {
    const ref = useRef(null)

    usePinnedAct(ref, scroller, 900, (tl, el) => {
        const label = el.querySelector('.showcase-label')
        const frame = el.querySelector('.showcase-frame')
        const img   = el.querySelector('.showcase-img')

        if (label) tl.fromTo(label, {opacity:0,y:20}, {opacity:1,y:0,duration:0.7,ease:'power2.out'}, 0)
        if (frame) tl.fromTo(frame, {opacity:0,scale:0.88,y:60}, {opacity:1,scale:1,y:0,duration:1.2,ease:'power3.out'}, 0.2)
        if (img)   tl.fromTo(img,   {scale:1.12}, {scale:1,duration:1.4,ease:'power2.out'}, 0.2)
    }, (stl, el) => {
        const frame = el.querySelector('.showcase-frame')
        const img   = el.querySelector('.showcase-img')
        if (frame) stl.fromTo(frame, { y: 40, rotate: -0.5 }, { y: -40, rotate: 0.5, ease: 'none' }, 0)
        if (img)   stl.fromTo(img,   { scale: 1.08 }, { scale: 1, ease: 'none' }, 0)
    })

    return (
        <div ref={ref} className={`act act-showcase ${bgClass}`}>
            <span className="panel-number">{project.index}</span>
            <div className="showcase-inner">
                {act.label && <p className="showcase-label">{act.label}</p>}
                <div className="showcase-frame">
                    <img
                        src={act.image}
                        alt={project.title}
                        className="showcase-img"
                        onClick={project.url ? () => window.open(project.url, '_blank') : undefined}
                        style={{ cursor: project.url ? 'pointer' : 'default' }}
                    />
                    <div className="corner tl"/><div className="corner tr"/>
                    <div className="corner bl"/><div className="corner br"/>
                </div>
            </div>
        </div>
    )
}

// ─── Act: Combined Experiment (comparison + timesteps) ───────────────────────
const ActExperiment = ({ project, act, scroller, bgClass }) => {
    const ref = useRef(null)
    usePinnedAct(ref, scroller, 900, (tl, el) => {
        tl.fromTo(el.querySelector('.exp-tag'),     {opacity:0,x:-16}, {opacity:1,x:0,duration:0.6,ease:'power2.out'},0)
        tl.fromTo(el.querySelector('.exp-label'),   {opacity:0,y:20},  {opacity:1,y:0,duration:0.7,ease:'power2.out'},0.15)
        tl.fromTo(el.querySelector('.exp-summary'), {opacity:0,y:14},  {opacity:1,y:0,duration:0.7,ease:'power2.out'},0.3)
        // Comparison table
        tl.fromTo(el.querySelector('.exp-comp-head'),       {opacity:0}, {opacity:1,duration:0.6,ease:'power2.out'},0.5)
        tl.fromTo(el.querySelectorAll('.exp-comp-row'),     {opacity:0,x:-16}, {opacity:1,x:0,stagger:0.09,duration:0.6,ease:'power2.out'},0.65)
        // Timesteps section
        tl.fromTo(el.querySelector('.exp-ts-intro'),        {opacity:0,y:12}, {opacity:1,y:0,duration:0.7,ease:'power2.out'},1.25)
        tl.fromTo(el.querySelector('.exp-ts-table'),        {opacity:0},      {opacity:1,duration:0.7,ease:'power2.out'},1.4)
        tl.fromTo(el.querySelectorAll('.ts-mini-callout'),  {opacity:0,y:20,scale:0.9}, {opacity:1,y:0,scale:1,stagger:0.18,duration:0.7,ease:'power3.out'},1.7)
        tl.fromTo(el.querySelectorAll('.callout-val'),      {color:'#8d8d8d'},{color:'#d10000',stagger:0.18,duration:0.6,ease:'power2.out'},1.95)
    }, (stl, el) => {
        // Scroll-linked: left and right columns drift at different rates
        const left  = el.querySelector('.exp-left')
        const right = el.querySelector('.exp-right')
        if (left)  stl.fromTo(left,  { y: 50 },  { y: -30, ease: 'none' }, 0)
        if (right) stl.fromTo(right, { y: 70 },  { y: -20, ease: 'none' }, 0)
    })

    const { comparison: cmp, timesteps: ts } = act

    return (
        <div ref={ref} className={`act act-experiment ${bgClass}`}>
            <span className="panel-number">{project.index}</span>
            <div className="act-experiment-inner">

                {/* ── Left column: header + comparison table ── */}
                <div className="exp-left">
                    <p className="exp-tag">{act.experimentTag}</p>
                    <p className="exp-label">{act.label}</p>
                    <p className="exp-summary">{act.summary}</p>

                    <div className="exp-comp-table">
                        <div className="exp-comp-head">
                            <span className="exp-metric-head">Metric</span>
                            {cmp.columns.map((col,i) => (
                                <span key={i} className={`exp-col-head col-${i}`}>{col}</span>
                            ))}
                        </div>
                        {cmp.rows.map((row,i) => (
                            <div key={i} className="exp-comp-row">
                                <span className="exp-metric">{row.metric}</span>
                                {row.values.map((val,j) => (
                                    <span key={j} className={`exp-val ${j===row.winner?'is-winner':''}`}>
                                        {j===row.winner && <span className={`win-dot col-${j}`}/>}
                                        {val}
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Right column: timesteps table + callouts ── */}
                <div className="exp-right">
                    <p className="exp-ts-label">Extended · Time Steps Trade-off</p>
                    <p className="exp-ts-intro">{ts.intro}</p>

                    <div className="exp-ts-table">
                        <div className="exp-ts-head">
                            {ts.columns.map((col,i) => (
                                <span key={i} className={i===0?'ts-t':'ts-d'}>{col}</span>
                            ))}
                        </div>
                        {ts.rows.map((row,i) => (
                            <div key={i} className={`exp-ts-row ${row.best?'is-best':''}`}>
                                <span className="ts-t">
                                    {row.best && <span className="best-pip"/>}
                                    T={row.label}
                                </span>
                                {row.values.map((val,j) => (
                                    <span key={j} className="ts-d">{val}</span>
                                ))}
                            </div>
                        ))}
                    </div>

                    <div className="ts-mini-callouts">
                        {ts.callouts.map((c,i) => (
                            <div key={i} className="ts-mini-callout">
                                <span className="callout-val">{c.value}</span>
                                <span className="callout-lbl">{c.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    )
}

// ─── Act: Details ─────────────────────────────────────────────────────────────
const ActDetails = ({ project, act, scroller, bgClass }) => {
    const ref = useRef(null)
    usePinnedAct(ref, scroller, 700, (tl, el) => {
        tl.fromTo(el.querySelectorAll('.details-label'), {opacity:0,x:-16}, {opacity:1,x:0,stagger:0.2,duration:0.7,ease:'power2.out'},0)
        tl.fromTo(el.querySelectorAll('.desc-line'),     {opacity:0,y:22},  {opacity:1,y:0,stagger:0.09,duration:0.7,ease:'power2.out'},0.2)
        tl.fromTo(el.querySelectorAll('.tech-tag'),      {opacity:0,y:14,scale:0.92}, {opacity:1,y:0,scale:1,stagger:0.06,duration:0.6,ease:'back.out(1.4)'},0.7)
    }, (stl, el) => {
        const left  = el.querySelector('.details-left')
        const right = el.querySelector('.details-right')
        if (left)  stl.fromTo(left,  { y: 40 }, { y: -30, ease: 'none' }, 0)
        if (right) stl.fromTo(right, { y: 60 }, { y: -20, ease: 'none' }, 0)
    })
    const sentences = act.leftText.match(/[^.!?]+[.!?]+/g) || [act.leftText]
    return (
        <div ref={ref} className={`act act-details ${bgClass}`}>
            <span className="panel-number">{project.index}</span>
            <div className="act-details-inner">
                <div className="details-left">
                    <p className="details-label">{act.leftLabel||'Overview'}</p>
                    <div className="details-desc">
                        {sentences.map((s,i) => <span className="desc-line" key={i}>{s.trim()} </span>)}
                    </div>
                </div>
                <div className="details-right">
                    <p className="details-label">{act.rightLabel||'Technologies'}</p>
                    <div className="tech-stack">
                        {project.acts[0].tech.map((t,i) => <span key={i} className="tech-tag">{t}</span>)}
                    </div>
                </div>
            </div>
        </div>
    )
}

// ─── Act: Stats ───────────────────────────────────────────────────────────────
const ActStats = ({ project, act, scroller, bgClass }) => {
    const ref = useRef(null)
    usePinnedAct(ref, scroller, 600, (tl, el) => {
        tl.fromTo(el.querySelector('.stats-eyebrow'), {opacity:0,y:16}, {opacity:1,y:0,duration:0.7,ease:'power2.out'},0)
        tl.fromTo(el.querySelectorAll('.stat-item'),  {opacity:0,y:50,scale:0.9}, {opacity:1,y:0,scale:1,stagger:0.2,duration:0.85,ease:'power3.out'},0.2)
        tl.fromTo(el.querySelectorAll('.stat-value'), {color:'#8d8d8d'}, {color:'#d10000',stagger:0.2,duration:0.6,ease:'power2.out'},0.55)
    }, (stl, el) => {
        const items = el.querySelectorAll('.stat-item')
        items.forEach((item, i) => {
            const offset = (i % 2 === 0) ? 30 : 50
            stl.fromTo(item, { y: offset }, { y: -offset * 0.6, ease: 'none' }, 0)
        })
    })
    return (
        <div ref={ref} className={`act act-highlight act-highlight--stats ${bgClass}`}>
            <span className="panel-number">{project.index}</span>
            <div className="stats-inner">
                <p className="stats-eyebrow">{act.label||'By the numbers'}</p>
                <div className="stats-grid">
                    {act.stats.map((s,i) => (
                        <div key={i} className="stat-item">
                            <span className="stat-value">{s.value}</span>
                            <span className="stat-label">{s.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

// ─── Act: Gallery (Parallax) ─────────────────────────────────────────────────
const ActGallery = ({ project, act, scroller, bgClass }) => {
    const ref = useRef(null)
    const lightboxRef = useRef(null)
    const [expandedSrc, setExpandedSrc] = useState(null)
    const images = act.images || project.images
    const colCount = images.length > 2 ? 3 : 2

    // Distribute images round-robin across columns
    const columns = Array.from({ length: colCount }, () => [])
    images.forEach((img, i) => columns[i % colCount].push(img))

    const galleryTl = useCallback((tl, el) => {
        // Label entrance
        tl.fromTo(el.querySelector('.gallery-eyebrow'),
            { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.85, ease: 'power3.out' }, 0)

        // Images: scale + rotation entrance
        tl.fromTo(el.querySelectorAll('.gallery-img-wrap'),
            { opacity: 0, scale: 0.85, rotation: 2 },
            { opacity: 1, scale: 1, rotation: 0, stagger: 0.15, duration: 1.0, ease: 'power3.out' }, 0.15)

        // Counter fade
        const counter = el.querySelector('.gallery-counter')
        if (counter) {
            tl.fromTo(counter, { opacity: 0 }, { opacity: 1, duration: 0.7, ease: 'power2.out' }, 0.4)
        }
    }, [])

    const galleryScrollTl = useCallback((stl, el) => {
        const cols = el.querySelectorAll('.gallery-col')
        const drifts = cols.length === 3
            ? [{ y: [80, -80], r: [2, -2] }, { y: [-60, 60], r: [-1.5, 1.5] }, { y: [90, -90], r: [1.8, -1.8] }]
            : [{ y: [60, -60], r: [1.5, -1.5] }, { y: [-50, 50], r: [-1, 1] }]

        cols.forEach((col, i) => {
            if (drifts[i]) {
                stl.fromTo(col, { y: drifts[i].y[0], rotation: drifts[i].r[0] },
                    { y: drifts[i].y[1], rotation: drifts[i].r[1], ease: 'none' }, 0)
            }
        })
    }, [])

    usePinnedAct(ref, scroller, 1400, galleryTl, galleryScrollTl)

    // ── Lightbox open ──
    const openLightbox = useCallback((src) => setExpandedSrc(src), [])

    // ── Lightbox close ──
    const closeLightbox = useCallback(() => {
        const lb = lightboxRef.current
        if (!lb) { setExpandedSrc(null); return }
        gsap.to(lb.querySelector('.lb-image'), {
            scale: 0.85, opacity: 0, filter: 'blur(10px)',
            duration: 0.45, ease: 'power2.in',
        })
        gsap.to(lb.querySelector('.lb-backdrop'), {
            opacity: 0, duration: 0.45, ease: 'power2.in',
            onComplete: () => {
                setExpandedSrc(null)
                ScrollTrigger.refresh()
            },
        })
    }, [])

    // Animate lightbox entrance
    useEffect(() => {
        if (!expandedSrc || !lightboxRef.current) return
        const lb = lightboxRef.current
        gsap.set(lb.querySelector('.lb-image'), { scale: 0.6, opacity: 0, filter: 'blur(14px)' })
        gsap.set(lb.querySelector('.lb-backdrop'), { opacity: 0 })
        gsap.to(lb.querySelector('.lb-backdrop'), { opacity: 1, duration: 0.5, ease: 'power2.out' })
        gsap.to(lb.querySelector('.lb-image'), {
            scale: 1, opacity: 1, filter: 'blur(0px)',
            duration: 0.7, ease: 'power3.out', delay: 0.07,
        })
    }, [expandedSrc])

    // Escape key to close
    useEffect(() => {
        if (!expandedSrc) return
        const onKey = (e) => { if (e.key === 'Escape') closeLightbox() }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [expandedSrc, closeLightbox])

    // Close lightbox on scroll (delay listener so pinned scrub doesn't trigger it immediately)
    useEffect(() => {
        if (!expandedSrc) return
        const scr = scroller || getScroller()
        if (!scr) return
        let listening = false
        const onScroll = () => { if (listening) closeLightbox() }
        const timer = setTimeout(() => {
            listening = true
            scr.addEventListener('scroll', onScroll, { once: true })
        }, 400)
        return () => { clearTimeout(timer); scr.removeEventListener('scroll', onScroll) }
    }, [expandedSrc, scroller, closeLightbox])

    return (
        <div ref={ref} className={`act act-gallery ${bgClass}`}>
            <span className="panel-number">{project.index}</span>
            <div className="gallery-inner">
                <p className="gallery-eyebrow">{act.label || 'Gallery'}</p>
                <div className={`gallery-columns cols-${colCount}`}>
                    {columns.map((col, ci) => (
                        <div key={ci} className="gallery-col">
                            {col.map((img, ii) => (
                                <div key={ii} className="image-frame gallery-img-wrap"
                                     onClick={() => openLightbox(img)}
                                     style={{ cursor: 'zoom-in' }}>
                                    <img src={img} alt={`${project.title} ${ci * columns[0].length + ii + 1}`} className="panel-image" />
                                    <div className="corner tl"/><div className="corner tr"/>
                                    <div className="corner bl"/><div className="corner br"/>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <span className="gallery-counter">
                    {String(images.length).padStart(2, '0')} visuals
                </span>
            </div>
            {expandedSrc && (
                <div ref={lightboxRef} className="gallery-lightbox" onClick={closeLightbox}>
                    <div className="lb-backdrop" />
                    <img src={expandedSrc} className="lb-image" alt="Expanded view" />
                </div>
            )}
        </div>
    )
}

// ─── Act: Fullpage (Cinematic Image Reveal) ──────────────────────────────────
const ActFullpage = ({ project, act, scroller, bgClass }) => {
    const ref = useRef(null)
    const images = act.images
    const pinDistance = images.length > 1 ? 1600 : 900

    usePinnedAct(ref, scroller, pinDistance, (tl, el) => {
        const items = el.querySelectorAll('.fp-image-wrap')
        const label = el.querySelector('.fp-label')

        // Label
        tl.fromTo(label, { opacity: 0, letterSpacing: '8px' },
            { opacity: 1, letterSpacing: '3px', ease: 'power2.out', duration: 0.7 }, 0)

        if (images.length === 1) {
            tl.fromTo(items[0],
                { opacity: 0, scale: 0.6 },
                { opacity: 1, scale: 1, ease: 'power3.out', duration: 1.25 }, 0.15)
            // Ken Burns slow drift
            tl.to(items[0],
                { scale: 1.06, ease: 'none', duration: 4 }, 1.4)
        } else {
            // Image 1 enter
            tl.fromTo(items[0],
                { opacity: 0, scale: 0.6 },
                { opacity: 1, scale: 1, ease: 'power3.out', duration: 1.1 }, 0.15)
            // Image 1 Ken Burns hold
            tl.to(items[0],
                { scale: 1.04, ease: 'none', duration: 2 }, 1.25)
            // Image 1 exit + Image 2 enter (crossfade)
            tl.to(items[0],
                { opacity: 0, scale: 1.08, ease: 'power2.in', duration: 0.85 }, 3.25)
            tl.fromTo(items[1],
                { opacity: 0, scale: 0.6 },
                { opacity: 1, scale: 1, ease: 'power3.out', duration: 1.1 }, 3.25)
            // Image 2 Ken Burns hold
            tl.to(items[1],
                { scale: 1.06, ease: 'none', duration: 2.8 }, 4.35)
        }
    }, (stl, el) => {
        // Scroll-linked slow scale drift on images
        const items = el.querySelectorAll('.fp-image-wrap')
        items.forEach(item => {
            stl.fromTo(item, { scale: 1 }, { scale: 1.08, ease: 'none' }, 0)
        })
    })

    return (
        <div ref={ref} className={`act act-fullpage ${bgClass}`}>
            <span className="panel-number">{project.index}</span>
            <div className="fp-inner">
                <p className="fp-label">{act.label}</p>
                <div className="fp-stack">
                    {images.map((img, i) => (
                        <div key={i} className="fp-image-wrap">
                            <img src={img} alt={`${project.title} ${i + 1}`} className="fp-image" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

// ─── Act: Quote ───────────────────────────────────────────────────────────────
const ActQuote = ({ project, act, scroller, bgClass }) => {
    const ref = useRef(null)
    usePinnedAct(ref, scroller, 600, (tl, el) => {
        tl.fromTo(el.querySelector('.quote-mark'), {opacity:0,scale:0.6}, {opacity:1,scale:1,duration:0.85,ease:'back.out(1.6)'},0)
        tl.fromTo(el.querySelector('.quote-text'), {opacity:0,y:30},     {opacity:1,y:0,duration:1.0,ease:'power3.out'},0.3)
        tl.fromTo(el.querySelector('.quote-attr'), {opacity:0},          {opacity:1,duration:0.7,ease:'power2.out'},0.7)
        if (el.querySelector('.flat-button')) {
            tl.fromTo(el.querySelector('.flat-button'), {opacity:0,y:16}, {opacity:1,y:0,duration:0.7,ease:'power2.out'},1.0)
        }
    }, (stl, el) => {
        const inner = el.querySelector('.quote-inner')
        if (inner) stl.fromTo(inner, { y: 30 }, { y: -30, ease: 'none' }, 0)
    })
    return (
        <div ref={ref} className={`act act-highlight act-highlight--quote ${bgClass}`}>
            <span className="panel-number">{project.index}</span>
            <div className="quote-inner">
                <span className="quote-mark">"</span>
                <blockquote className="quote-text">{act.text}</blockquote>
                <p className="quote-attr">— {act.attribution}</p>
                {project.url && <a className="flat-button" href={project.url} target="_blank" rel="noopener noreferrer">VIEW LIVE</a>}
            </div>
        </div>
    )
}

// ─── Act Router ───────────────────────────────────────────────────────────────
const ActRouter = ({ project, act, actIndex, scroller }) => {
    const bgClass = ACT_BG_CYCLE[actIndex % ACT_BG_CYCLE.length]
    const props   = { project, act, scroller, bgClass }
    switch (act.type) {
        case 'hero':       return <ActHero       {...props}/>
        case 'showcase':   return <ActShowcase  {...props}/>
        case 'experiment': return <ActExperiment {...props}/>
        case 'details':    return <ActDetails    {...props}/>
        case 'gallery':    return <ActGallery   {...props}/>
        case 'fullpage':   return <ActFullpage  {...props}/>
        case 'stats':      return <ActStats      {...props}/>
        case 'quote':      return <ActQuote      {...props}/>
        default:           return null
    }
}

// ─── Project Entry ────────────────────────────────────────────────────────────
const ProjectEntry = ({ project }) => {
    const wrapperRef = useRef(null)

    useEffect(() => {
        const scr = getScroller()
        if (!scr || !wrapperRef.current) return
        const ctx = gsap.context(() => {
            const acts = gsap.utils.toArray('.act', wrapperRef.current)
            acts.forEach((act, i) => {
                if (i === 0) return
                gsap.fromTo(act,
                    { clipPath: 'inset(100% 0% 0% 0%)' },
                    { clipPath: 'inset(0% 0% 0% 0%)', ease: 'power1.inOut',
                      scrollTrigger: { trigger: acts[i-1], scroller: scr, start: 'bottom bottom', end: 'bottom 20%', scrub: 1.2 } }
                )
            })
        }, wrapperRef)
        return () => ctx.revert()
    }, [])

    const scroller = getScroller()
    return (
        <div ref={wrapperRef} className="project-entry" id={`project-${project.id}`}>
            {project.acts.map((act, i) => (
                <ActRouter key={i} project={project} act={act} actIndex={i} scroller={scroller}/>
            ))}
        </div>
    )
}

// // ─── Scroll Landing ───────────────────────────────────────────────────────────
// const ScrollLanding = () => {
//     const landingRef = useRef(null)
//     const tickerRef  = useRef(null)
//     const arrowsRef  = useRef(null)

//     useEffect(() => {
//         const el       = landingRef.current
//         const scroller = getScroller()
//         if (!el || !scroller) return

//         const ctx = gsap.context(() => {
//             gsap.fromTo('.sl-count',       { opacity:0, y:50 },  { opacity:1, y:0, ease:'power3.out', duration:0.9, delay:0.9 })
//             gsap.fromTo('.sl-divider',     { scaleX:0 },         { scaleX:1, ease:'power2.out', duration:0.7, delay:1.1 })
//             gsap.fromTo('.sl-tagline',     { opacity:0, y:20 },  { opacity:1, y:0, ease:'power2.out', duration:0.6, delay:1.2 })
//             gsap.fromTo('.sl-ticker-wrap', { opacity:0 },        { opacity:1, ease:'power2.out', duration:0.7, delay:1.35 })
//             gsap.fromTo('.sl-scroll-label',{ opacity:0 },        { opacity:1, ease:'power2.out', duration:0.6, delay:1.5 })
//             gsap.fromTo('.sl-arrow',       { opacity:0, y:-8 },  { opacity:1, y:0, ease:'power2.out', duration:0.4, stagger:0.1, delay:1.6 })
//         }, el)

//         // Ticker: duplicate track for seamless loop
//         const ticker = tickerRef.current
//         if (ticker) {
//             const track = ticker.querySelector('.sl-ticker-track')
//             if (track) {
//                 const clone  = track.cloneNode(true)
//                 ticker.appendChild(clone)
//                 const trackW = track.scrollWidth
//                 gsap.to(ticker.querySelectorAll('.sl-ticker-track'), {
//                     x: `-=${trackW}`, ease:'none', duration:24,
//                     repeat:-1,
//                     modifiers: { x: gsap.utils.unitize(x => parseFloat(x) % trackW) }
//                 })
//             }
//         }

//         // Arrows: cascading bounce
//         if (arrowsRef.current) {
//             gsap.to(arrowsRef.current.querySelectorAll('.sl-arrow'), {
//                 y:10, ease:'sine.inOut', duration:0.65,
//                 stagger:0.18, yoyo:true, repeat:-1
//             })
//         }

//         // Fade out when scrolled down, fade back in when returned to top
//         let hidden = false
//         const onScroll = () => {
//             if (scroller.scrollTop > 60 && !hidden) {
//                 hidden = true
//                 gsap.to(el, { opacity:0, y:-24, duration:0.7, ease:'power2.in',
//                     onComplete: () => { el.style.pointerEvents = 'none' }
//                 })
//             } else if (scroller.scrollTop <= 60 && hidden) {
//                 hidden = false
//                 el.style.pointerEvents = ''
//                 gsap.to(el, { opacity:1, y:0, duration:0.65, ease:'power2.out' })
//             }
//         }
//         scroller.addEventListener('scroll', onScroll, { passive:true })
//         return () => { ctx.revert(); scroller.removeEventListener('scroll', onScroll) }
//     }, [])

//     return (
//         <div ref={landingRef} className="scroll-landing">
//             {/* Project count + label */}
//             <div className="sl-top">
//                 <span className="sl-count">{String(projectsData.length).padStart(2, '0')}</span>
//                 <div className="sl-divider"/>
//                 <p className="sl-tagline">selected works</p>
//             </div>

//             {/* Auto-scrolling ticker of project names */}
//             <div className="sl-ticker-wrap">
//                 <div ref={tickerRef} className="sl-ticker">
//                     <div className="sl-ticker-track">
//                         {projectsData.map((p, i) => (
//                             <span key={i} className="sl-ticker-item" onClick={() => {
//                                 const scroller = getScroller()
//                                 const entry = document.getElementById(`project-${p.id}`)
//                                 const target = entry && entry.querySelector('.act')
//                                 if (scroller && target) {
//                                     const rect = target.getBoundingClientRect()
//                                     const scrollerRect = scroller.getBoundingClientRect()
//                                     // Land 120px past the act top so GSAP's scrubbed timeline
//                                     // has enough progress to show the hero content immediately
//                                     scroller.scrollTo({ top: scroller.scrollTop + rect.top - scrollerRect.top + 400, behavior: 'smooth' })
//                                 }
//                             }}>
//                                 <span className="sl-ticker-index">{p.index}</span>
//                                 {p.title}
//                                 <span className="sl-ticker-sep">·</span>
//                             </span>
//                         ))}
//                     </div>
//                 </div>
//             </div>

//             {/* Scroll cue: label + cascading chevrons */}
//             <div className="sl-scroll-cue">
//                 <span className="sl-scroll-label">scroll to explore</span>
//                 <div ref={arrowsRef} className="sl-arrow-stack">
//                     {[0, 1, 2].map(i => (
//                         <svg key={i} className="sl-arrow" viewBox="0 0 24 12" fill="none">
//                             <polyline points="2,2 12,10 22,2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
//                         </svg>
//                     ))}
//                 </div>
//             </div>
//         </div>
//     )
// }

// ─── Scroll Landing ───────────────────────────────────────────────────────────
const ScrollLanding = () => {
  const landingRef = useRef(null)
  const tickerRef = useRef(null)
  const arrowsRef = useRef(null)
  
  // Ref to differentiate between a click and a drag
  const isDraggingRef = useRef(false)

  useEffect(() => {
    const el = landingRef.current
    const scroller = getScroller()
    if (!el || !scroller) return

    const ctx = gsap.context(() => {
      gsap.fromTo('.sl-count', { opacity:0, y:50 }, { opacity:1, y:0, ease:'power3.out', duration:1.2, delay:1.0 })
      gsap.fromTo('.sl-divider', { scaleX:0 }, { scaleX:1, ease:'power2.out', duration:1.0, delay:1.3 })
      gsap.fromTo('.sl-tagline', { opacity:0, y:20 }, { opacity:1, y:0, ease:'power2.out', duration:0.85, delay:1.5 })
      gsap.fromTo('.sl-ticker-wrap', { opacity:0 }, { opacity:1, ease:'power2.out', duration:1.0, delay:1.7 })
      gsap.fromTo('.sl-scroll-label',{ opacity:0 }, { opacity:1, ease:'power2.out', duration:0.85, delay:1.9 })
      gsap.fromTo('.sl-arrow', { opacity:0, y:-8 }, { opacity:1, y:0, ease:'power2.out', duration:0.6, stagger:0.15, delay:2.1 })
    }, el)

    // Ticker logic: proxy-driven seamless loop + drag events
    const ticker = tickerRef.current
    let tickerTween, onDown, onMove, onUp

    if (ticker) {
      const track = ticker.querySelector('.sl-ticker-track')
      if (track) {
        const clone = track.cloneNode(true)
        ticker.appendChild(clone)
        const trackW = track.scrollWidth
        const tracks = ticker.querySelectorAll('.sl-ticker-track')

        ticker.style.cursor = 'grab'
        const proxy = { x: 0 }

        // Mathematically locks our position to always perfectly loop
        const wrap = (val) => {
          let v = val % trackW
          if (v > 0) v -= trackW
          return v
        }

        tickerTween = gsap.fromTo(proxy, 
          { x: 0 }, 
          {
            x: -trackW,
            ease: 'none',
            duration: 24,
            repeat: -1,
            onUpdate: () => gsap.set(tracks, { x: wrap(proxy.x) })
          }
        )

        let isDown = false
        let startX = 0
        let startProxyX = 0

        onDown = (e) => {
          isDown = true
          isDraggingRef.current = false
          ticker.style.cursor = 'grabbing'
          tickerTween.pause()
          startX = e.pageX || (e.touches && e.touches[0].pageX)
          startProxyX = proxy.x
        }

        onMove = (e) => {
          if (!isDown) return
          const currentX = e.pageX || (e.touches && e.touches[0].pageX)
          const dx = currentX - startX

          // If moved more than 3px, treat as a drag, not a click
          if (Math.abs(dx) > 3) {
            isDraggingRef.current = true
          }

          // Prevent page scroll while dragging the ticker horizontally
          if (isDraggingRef.current && e.cancelable) {
            e.preventDefault()
          }

          proxy.x = startProxyX + dx
          gsap.set(tracks, { x: wrap(proxy.x) })
        }

        onUp = () => {
          if (!isDown) return
          isDown = false
          ticker.style.cursor = 'grab'

          // Kill old tween and reconstruct it from our new dropped position
          tickerTween.kill()
          tickerTween = gsap.fromTo(proxy,
            { x: proxy.x },
            {
              x: proxy.x - trackW,
              ease: 'none',
              duration: 24,
              repeat: -1,
              onUpdate: () => gsap.set(tracks, { x: wrap(proxy.x) })
            }
          )

          // Short delay ensures the onClick handler checks the flag before it resets
          setTimeout(() => {
            isDraggingRef.current = false
          }, 50)
        }

        ticker.addEventListener('mousedown', onDown)
        ticker.addEventListener('touchstart', onDown, { passive: true })
        window.addEventListener('mousemove', onMove)
        window.addEventListener('touchmove', onMove, { passive: false })
        window.addEventListener('mouseup', onUp)
        window.addEventListener('touchend', onUp)
      }
    }

    // Arrows: cascading bounce
    if (arrowsRef.current) {
      gsap.to(arrowsRef.current.querySelectorAll('.sl-arrow'), {
        y:10, ease:'sine.inOut', duration:0.9,
        stagger:0.25, yoyo:true, repeat:-1
      })
    }

    // Fade out when scrolled down, fade back in when returned to top
    let hidden = false
    const onScroll = () => {
      if (scroller.scrollTop > 60 && !hidden) {
        hidden = true
        gsap.to(el, { opacity:0, y:-24, duration:0.7, ease:'power2.in',
          onComplete: () => { el.style.pointerEvents = 'none' }
        })
      } else if (scroller.scrollTop <= 60 && hidden) {
        hidden = false
        el.style.pointerEvents = ''
        gsap.to(el, { opacity:1, y:0, duration:0.65, ease:'power2.out' })
      }
    }
    scroller.addEventListener('scroll', onScroll, { passive:true })

    // Cleanup phase
    return () => {
      ctx.revert()
      scroller.removeEventListener('scroll', onScroll)
      if (tickerTween) tickerTween.kill()
      if (ticker && onDown) {
        ticker.removeEventListener('mousedown', onDown)
        ticker.removeEventListener('touchstart', onDown)
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('touchmove', onMove)
        window.removeEventListener('mouseup', onUp)
        window.removeEventListener('touchend', onUp)
      }
    }
  }, [])

  return (
    <div ref={landingRef} className="scroll-landing">
      {/* Project count + label */}
      <div className="sl-top">
        <span className="sl-count">{String(projectsData.length).padStart(2, '0')}</span>
        <div className="sl-divider"/>
        <p className="sl-tagline">selected works</p>
      </div>

      {/* Auto-scrolling ticker of project names */}
      {/* Inline userSelect completely strips text highlighting capability while interacting */}
      <div className="sl-ticker-wrap" style={{ userSelect: 'none' }}>
        <div ref={tickerRef} className="sl-ticker">
          <div className="sl-ticker-track">
            {projectsData.map((p, i) => (
              <span key={i} className="sl-ticker-item" onClick={(e) => {
                // If they dragged the element, abort click execution
                if (isDraggingRef.current) {
                  e.preventDefault()
                  return
                }
                const scroller = getScroller()
                const entry = document.getElementById(`project-${p.id}`)
                const target = entry && entry.querySelector('.act')
                if (scroller && target) {
                  const rect = target.getBoundingClientRect()
                  const scrollerRect = scroller.getBoundingClientRect()
                  scroller.scrollTo({ top: scroller.scrollTop + rect.top - scrollerRect.top + 400, behavior: 'smooth' })
                }
              }}>
                <span className="sl-ticker-index">{p.index}</span>
                {p.title}
                <span className="sl-ticker-sep">·</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue: label + cascading chevrons */}
      <div className="sl-scroll-cue">
        <span className="sl-scroll-label">scroll to explore</span>
        <div ref={arrowsRef} className="sl-arrow-stack">
          {[0, 1, 2].map(i => (
            <svg key={i} className="sl-arrow" viewBox="0 0 24 12" fill="none">
              <polyline points="2,2 12,10 22,2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Root ─────────────────────────────────────────────────────────────────────
const Project = () => {
    const [letterClass, setLetterClass] = useState('text-animate')
    const pageTitle  = ['P','r','o','j','e','c','t','s']
    const wrapperRef = useRef(null)

    useEffect(() => {
        const t = setTimeout(() => setLetterClass('text-animate-hover'), 2400)
        return () => clearTimeout(t)
    }, [])

    useEffect(() => {
        const scroller = getScroller()
        if (!scroller || !wrapperRef.current) return
        const ctx = gsap.context(() => {
            const entries = gsap.utils.toArray('.project-entry', wrapperRef.current)
            entries.forEach((entry, i) => {
                if (i === 0) return
                gsap.fromTo(entry,
                    { clipPath: 'inset(100% 0% 0% 0%)' },
                    { clipPath: 'inset(0% 0% 0% 0%)', ease: 'power2.inOut',
                      scrollTrigger: { trigger: entries[i-1], scroller, start: 'bottom bottom', end: 'bottom 10%', scrub: 1.4 } }
                )
            })
        }, wrapperRef)
        return () => ctx.revert()
    }, [])

    return (
        <>
            <div className="container project-page" ref={wrapperRef}>
                <div className="page-title-wrapper">
                    <h1 className="page-title">
                        <AnimatedLetters letterClass={letterClass} strArray={pageTitle} idx={15} showCursor/>
                    </h1>
                </div>
                <ScrollLanding/>
                <div className="projects-root">
                    {projectsData.map(project => (
                        <ProjectEntry key={project.id} project={project}/>
                    ))}
                </div>
            </div>
            <Loader type="pacman"/>
        </>
    )
}

export default Project